// Words for the Developer contract's codes (apiv2/developer.openapi.yaml). developerContract.test.js
// checks that every enum value of the contract has its words here; an unknown value (a newer
// backend) is shown as it is.

export type Tone = 'success' | 'error' | 'warning' | 'info' | 'neutral';

export interface Label {
    label: string;
    tone: Tone;
    hint?: string;
}

const lookup = (table: Record<string, Label>, value: string | null | undefined): Label => (
    (value != null && table[value]) || { label: value == null ? '—' : String(value), tone: 'neutral' }
);

export const DEVELOPER_PAGES: Record<string, string> = {
    overview: 'Overview',
    api_keys: 'API keys',
    documentation: 'Documentation',
    request_log: 'Request log',
    imports: 'Imports',
    outbox: 'Outbox',
    webhook_deliveries: 'Webhooks',
    vendor_calls: 'Vendor calls',
    metrics: 'Metrics',
    activity: 'Activity',
    integrations: 'Integrations',
};

export const ENVIRONMENTS: Record<string, string> = {
    production: 'Production',
    sandbox: 'Sandbox',
};

export const OUTBOUND_MODES: Record<string, { label: string; hint: string }> = {
    outbox: { label: 'Outbox only', hint: 'Nothing is delivered: every message is kept in the outbox.' },
    allowlist: { label: 'Allowlist', hint: 'Only the testers listed below receive messages; the rest is kept in the outbox.' },
    allow_all: { label: 'Allow all', hint: 'Everyone receives messages, as in production.' },
};

export const REQUEST_STATES: Record<string, Label> = {
    queued: { label: 'Queued', tone: 'info', hint: 'Accepted, not delivered to the backend yet (or being retried).' },
    held: { label: 'Held', tone: 'warning', hint: 'The circuit breaker paused the sandbox: stored until a reset delivers or drops it.' },
    rejected: { label: 'Rejected', tone: 'error', hint: 'Refused at the edge, dropped at a breaker reset, or answered 4xx.' },
    processing: { label: 'Processing', tone: 'info', hint: 'Accepted; its bookings (or its import) are not all done yet.' },
    processed: { label: 'Processed', tone: 'success', hint: "Accepted and done; each booking's own result is listed." },
    failed: { label: 'Failed', tone: 'error', hint: 'The backend answered 5xx, or the edge stopped trying to deliver it.' },
};
export const requestState = (value: string | null | undefined) => lookup(REQUEST_STATES, value);

export const STATE_REASONS: Record<string, string> = {
    rate_limited: 'Rate limited',
    quota_exceeded: 'Quota reached',
    too_large: 'Too large',
    sandbox_suspended: 'Sandbox suspended',
    admission_unavailable: 'Ingress unavailable',
    breaker_paused: 'Circuit breaker paused',
    dropped_on_reset: 'Dropped at the breaker reset',
    client_error: 'Client error',
    server_error: 'Server error',
    delivery_failed: 'Delivery failed',
    bookings_pending: 'Bookings still processing',
    import_running: 'Import running',
};
export const stateReason = (value: string | null | undefined) => (value ? STATE_REASONS[value] || value : null);

export const BOOKING_OUTCOMES: Record<string, Label> = {
    pending: { label: 'Pending', tone: 'info' },
    created: { label: 'Created', tone: 'success' },
    updated: { label: 'Updated', tone: 'success' },
    cancelled: { label: 'Cancelled', tone: 'neutral' },
    unchanged: { label: 'Unchanged', tone: 'neutral' },
    skipped: { label: 'Skipped', tone: 'warning' },
    failed: { label: 'Failed', tone: 'error' },
};
export const bookingOutcome = (value: string | null | undefined) => lookup(BOOKING_OUTCOMES, value);

// SBX plan D5/D9: a sandbox's sends are expected outcomes, shown neutral -- never as errors.
export const OUTBOX_OUTCOMES: Record<string, Label> = {
    sent_allowlisted: { label: 'Sent (allowlisted)', tone: 'neutral' },
    partially_sent_allowlisted: { label: 'Partially sent (allowlisted)', tone: 'neutral' },
    captured: { label: 'Captured (sandbox)', tone: 'neutral' },
    sent: { label: 'Sent (allow all)', tone: 'neutral' },
    sent_client_endpoint: { label: 'Sent to your endpoint', tone: 'neutral' },
};
export const outboxOutcome = (value: string | null | undefined) => lookup(OUTBOX_OUTCOMES, value);

export const OUTBOX_CHANNELS: Record<string, string> = {
    email: 'E-mail',
    sms: 'SMS',
    whatsapp: 'WhatsApp',
    airbnb: 'Airbnb',
    push: 'Push',
    branchio: 'Branch.io link',
    sns: 'SNS',
    http_webhook: 'HTTP webhook',
    zapier: 'Zapier',
};

export const OUTBOX_REASONS: Record<string, string> = {
    allowlisted: 'on the allowlist',
    allow_all: 'allow all',
    client_endpoint: 'your own endpoint',
    mode_outbox: 'outbox mode',
    not_on_allowlist: 'not on the allowlist',
    phone_needs_own_number: "needs the sandbox's own phone number",
    destination_not_allowlistable: 'cannot be allowlisted',
    reserved_destination: 'reserved destination',
    sandbox_lane: 'sandbox lane',
    no_sandbox_branch_key: 'no sandbox Branch.io key',
    production_branch_key: "production's Branch.io key",
    own_branch_key: "the sandbox's own Branch.io key",
    placeholder_no_keys: 'no Branch.io keys of its own: placeholder link',
    branch_error: 'Branch.io refused the keys or failed: placeholder link',
    vendor_support: 'vendor support e-mail (never sent from a sandbox)',
};

export const SEND_KINDS: Record<string, string> = {
    automatic: 'Automatic',
    user_initiated: 'User-initiated',
    account: 'Account',
    internal_report: 'Internal report',
};

export const WEBHOOK_STATUSES: Record<string, Label> = {
    delivered: { label: 'Delivered', tone: 'success' },
    failed: { label: 'Failed', tone: 'error', hint: 'Another answer, a timeout or a connection error.' },
    refused: { label: 'Refused', tone: 'error', hint: 'The address resolves to a private, link-local or metadata address: never called.' },
    captured: { label: 'Captured (sandbox)', tone: 'neutral' },
    skipped: { label: 'Skipped', tone: 'neutral', hint: 'A duplicate delivery was skipped.' },
    policy_rejected: { label: 'Rejected by policy', tone: 'neutral' },
};
export const webhookStatus = (value: string | null | undefined) => lookup(WEBHOOK_STATUSES, value);

export const WEBHOOK_KINDS: Record<string, string> = {
    delivery: 'Delivery',
    redelivery: 'Redelivery',
    test: 'Test event',
};

export const VENDOR_CALL_OUTCOMES: Record<string, Label> = {
    read: { label: 'Read', tone: 'neutral' },
    allowed: { label: 'Allowed write', tone: 'warning' },
    refused: { label: 'Refused write', tone: 'neutral' },
};
export const vendorCallOutcome = (value: string | null | undefined) => lookup(VENDOR_CALL_OUTCOMES, value);

export const VENDOR_CALL_REASONS: Record<string, string> = {
    sandbox_vendor_write: 'A sandbox does not write to the vendor',
    sandbox_production_credential: "Production's credential",
    sandbox_suspended: 'Sandbox suspended',
};

export const SEVERITIES: Record<string, 'info' | 'warning' | 'error'> = {
    info: 'info',
    warning: 'warning',
    critical: 'error',
};

export const BREAKER_REASONS: Record<string, string> = {
    failures: 'too many failed requests',
    rate_limited: 'too many requests answered 429',
};

// Refusals the pages word themselves; for these the server's message wins when it sends one, except the
// codes in OWN_WORDS, whose server text is generic (DRF's typed denial) or not meant for a client (the mirror's
// refusals name a platform switch or a request field).
export const OWN_WORDS = new Set([
    'sandbox_suspended', 'query_timeout', 'invalid_body',
    'sandbox_production_data_disabled', 'sandbox_mirror_no_production', 'sandbox_mirror_invalid',
]);

export const ERROR_CODES: Record<string, string> = {
    forbidden: 'You do not have permission to do this.',
    not_sandbox: 'This exists only on a sandbox.',
    sandbox_outbound_invalid: 'Some entries were refused.',
    sandbox_allow_all_production_key: "Allow all is not possible while the sandbox uses a production vendor key.",
    sandbox_suspension_locked: 'Curated Planet suspended this sandbox; only they can resume it.',
    sandbox_breaker_invalid: 'That reset is not possible.',
    request_not_found: 'No such request (unknown, expired, or not this tenant’s).',
    not_replayable: 'This request cannot be replayed.',
    // F2's kill switch refuses every action of a suspended sandbox (423); its reads still answer.
    sandbox_suspended: 'The sandbox is suspended, so nothing can be changed or sent from here. Resume it on the Overview first; everything stays readable.',
    unknown_event: 'Unknown event.',
    not_redeliverable: 'This delivery can no longer be sent again.',
    not_found: 'Not found.',
    invalid_filter: 'A filter is not valid.',
    query_timeout: 'The search took too long. Narrow it: add a filter or a shorter time window.',
    invalid_body: 'The change could not be read by the server. Reload the page and try again.',
    // Sandbox sign-in from production (DSEC D3).
    mfa_required: 'Opening the sandbox needs a sign-in with two-step verification (or single sign-on). Sign in again that way, then try again.',
    no_sandbox: 'Your company has no sandbox. Ask Curated Planet to create one.',
    target_not_found: 'That user can no longer sign in to the sandbox.',
    no_sandbox_admin: 'The sandbox has no administrator to sign in as. Use “Open sandbox as…” to choose a user.',
    not_permitted: 'You do not have the permission for this: it needs “Developer: sign in to the sandbox” (ask an administrator).',
    signin_unavailable: 'The sandbox sign-in could not be recorded, so it was not made. Try again in a moment.',
    // "Add sandbox admin" from production (DSEC D6).
    sandbox_admin_exists: 'The sandbox already has an admin: open it with “Open sandbox”. Curated Planet can add more.',
    email_taken: 'A user of this sandbox already has this e-mail address: choose another one.',
    email_invalid: 'That is not an e-mail address.',
    name_required: 'Give the admin’s first and last name (at most 150 characters each).',
    password_weak: 'The password is too weak.',
    rate_limited: 'Too many tries for now. Try again later.',
    sandbox_admin_unavailable: 'The new admin could not be recorded, so it was not added. Try again in a moment.',
    // The sandbox's own SMS number (DSEC D5)
    sms_unavailable: 'Setting up SMS is not available here yet. Ask Curated Planet.',
    sms_busy: 'An SMS set-up or removal is already running. Try again in a moment.',
    sms_removing: 'A removal of SMS did not finish: remove it again first.',
    sms_configured_elsewhere: 'Curated Planet set up this sandbox’s messaging by hand: it is not changed from here.',
    sms_franchise_child: 'Set up SMS on the parent sandbox.',
    sms_no_number_available: 'No phone number is for sale right now. Try again later.',
    sms_twilio_error: 'Twilio could not finish. Try again: it continues where it stopped.',
    sms_not_set_up: 'Set up SMS first: the sandbox has no number to send from.',
    sms_remove_unsafe: 'The number or chat service is also used elsewhere: Curated Planet will check it.',
    invalid_phone: 'Give the phone in international format, e.g. +14155550123.',
    sandbox_allow_all_production_data: 'Allow all is not possible: with its own number, this sandbox would text the real guests of its production data. Choose Allowlist or Outbox.',
    inbound_number_taken: 'That number is already used by another operator.',
    sandbox_platform_number: 'That is Curated Planet’s own number: a sandbox needs a number of its own.',
    sms_set_up_limit: 'SMS was set up too many times for this sandbox recently: each set-up buys a new number. Ask Curated Planet.',
    sms_test_limit: 'Too many test SMS this hour: try again later.',
    metrics_unavailable: 'Metrics are temporarily unavailable. Try again in a minute.',
    // API keys (DSEC D1).
    api_credential_revoked: 'This key is revoked: a revoked key cannot be changed.',
    api_credential_rotated: 'This key was rotated and works only until its grace period ends. Change the new key instead.',
    api_credential_inactive: 'Only an active, unexpired key can be rotated. Create a new key instead.',
    api_credential_in_use: 'A webhook subscription uses this key and rotates it itself. Ask Curated Planet support.',
    api_credential_not_rotatable: 'This key cannot be rotated here.',
    // Developer › Integrations (DSEC INT).
    integration_exists: 'This booking system is connected already: add an account to it instead.',
    system_not_addable: 'This booking system cannot be added here: Curated Planet support connects it for you.',
    inherited_from_parent: "This connection is your franchise's: change it from the parent company.",
    oauth_not_available: 'Connecting by signing in is not available here: use the API keys instead.',
    unknown_system: 'Unknown booking system.',
    invalid_credentials: 'Some credentials are missing or not valid: nothing was saved.',
    invalid_settings: 'Some settings were refused; nothing was changed.',
    not_supported: 'This is not possible for this connection.',
    confirmation_mismatch: 'The typed name does not match: nothing was removed.',
    vendor_error: 'The booking system refused or failed, so nothing was changed. Try again later.',
    managed_by_curated_planet: 'Curated Planet manages the settings of this booking system: ask support.',
    config_modified: 'Someone changed this connection at the same time: reload the page and try again.',
    account_exists: 'An account with this slug exists already.',
    invalid_slug: 'The slug is not valid: lowercase letters, digits, - and _, starting with a letter or a digit.',
    default_account: 'The Default account goes with the connection only.',
    account_not_found: 'No such account: it may have been removed. Reload the page.',
    integration_not_found: 'No such connection: it may have been removed. Reload the page.',
    no_token: 'No webhook token is set for this account.',
    vendor_writes_refused: 'A sandbox never writes at the booking system.',
    no_credentials: 'Set the credentials first.',
    metrics_off: 'Webhook graphs need the developer metrics, which are not switched on.',
    // Developer › Integrations, I3's final codes (the server's own message is shown when it sends one).
    credentials_reach_no_company: 'These credentials reach no company at the booking system: check that you copied them in full.',
    credential_rejected: 'The booking system did not accept these credentials: check that you copied them in full.',
    account_ambiguous: 'The booking system could not tell which company these credentials belong to: ask Curated Planet support.',
    vendor_unreachable: 'The booking system could not be reached, so nothing was saved. Try again in a moment.',
    vendor_busy: 'The booking system is busy right now, so nothing was saved. Try again in a minute.',
    signup_unavailable: 'Connecting this booking system is not available right now. Try again later.',
    subscriptions_not_removed: 'A notification subscription could not be removed at the booking system, so nothing was removed. Try again later or ask Curated Planet support.',
    credentials_not_client_managed: 'Curated Planet manages these credentials: ask support to change them.',
    credentials_on_named_accounts: 'This booking system keeps its credentials on its accounts: replace them on an account.',
    credentials_read_only: "These credentials come from the booking system's app: connect again there to change them.",
    account_id_in_use: 'Another account of this connection has this account ID already.',
    account_add_not_supported: 'This connection keeps its credentials on the Default account, so it takes no other account: ask Curated Planet support.',
    use_connection_remove: "This is the connection's last account: remove the connection instead.",
    sandbox_production_key_refused: 'This sandbox may not use a production key: use test credentials.',
    // The tag-category grid (backend plan 2026-10-29 sdtc WP-G, WP-J).
    defaults_conflict: 'The default tag categories were changed elsewhere since you loaded them.',
    defaults_invalid: "The default tag categories can't be saved as they are.",
    inherited_default: "This default comes from your franchise's parent company: change it there.",
    seed_not_applicable: 'Adding the recommended defaults does not apply to this company.',
    preview_busy: 'Busy — someone is saving the default tag categories right now. Try again in a moment.',
    permission_denied: 'You do not have permission to change this.',
    no_access_group: 'Your account has no access group yet: ask an administrator to add you to one.',
    // The sandbox's webhook mirror (MIRROR-TOGGLE plan v3); `sandbox_allow_all_production_data` is above.
    sandbox_production_data_disabled: "Mirroring production's webhooks is not available yet: Curated Planet has not switched on production data for sandboxes.",
    sandbox_mirror_no_production: 'This sandbox belongs to no production company, so there are no production webhooks to copy.',
    sandbox_mirror_invalid: 'The switch was refused. Try again, and contact Curated Planet if it keeps happening.',
};

/** Why the webhook mirror cannot be switched on now (DeveloperMirror.unavailable_reason). */
export const MIRROR_UNAVAILABLE_REASONS: Record<string, string> = {
    sandbox_production_data_disabled: 'Not available yet: Curated Planet has not switched on production data for sandboxes.',
    sandbox_mirror_no_production: 'Not available: this sandbox belongs to no production company, so there are no production webhooks to copy.',
};
export const mirrorUnavailableReason = (value: string | null | undefined) => (
    (value && MIRROR_UNAVAILABLE_REASONS[value]) || `Not available right now${value ? ` (${value})` : ''}.`
);

/** Booking systems by their integration code, as clients know them. */
export const INTEGRATION_NAMES: Record<string, string> = {
    bokun: 'Bókun',
    fareharbor: 'FareHarbor',
    intrepid: 'Intrepid',
    juniper: 'Juniper',
    prioticket: 'Prioticket',
    rezdy: 'Rezdy',
    salesforce: 'Salesforce',
    tourcms: 'TourCMS',
    trekksoft: 'TrekkSoft',
    ventrata: 'Ventrata',
};
export const integrationName = (code: string) => INTEGRATION_NAMES[code] || code;

/** Copies the sandbox skips for want of production's vendor key (DeveloperMirror.needs_vendor_key, ESB-FHK). */
export const mirrorNeedsVendorKey = (integration: string) => (
    `Copies from ${integrationName(integration)} need production's vendor key: ask Curated Planet to switch it on.`
);

/** A mirrored account, `<integration>:<account>`: "fareharbor (default account)", "ventrata acme-eu". */
export const mirrorAccount = (key: string) => {
    const [integration, ...rest] = key.split(':');
    const account = rest.join(':');
    return !account || account === '_default' ? `${integration} (default account)` : `${integration} ${account}`;
};

// ---- API keys (DSEC D1) ----------------------------------------------------------------------------

/** A key's scope: its name and what it is for. */
export const API_KEY_SCOPES: Record<string, { label: string; hint: string }> = {
    tour_operator: { label: 'Import API', hint: 'Sends bookings, tours and users to Curated Planet and reads them back (import/query, MCP).' },
    webhooks: { label: 'Webhooks', hint: "A booking system's calls into Curated Planet (its webhooks and notifications)." },
    reports: { label: 'Reports (read-only)', hint: 'Reads the reports API only, for your reporting tools. It cannot change anything.' },
    client: { label: 'Client', hint: 'A platform key for an app client.' },
    admin: { label: 'Admin', hint: 'A platform administration key.' },
    partner_dev: { label: 'Partner developer', hint: "An integration partner's developer key on its sandbox." },
};

export const API_KEY_STATUSES: Record<string, Label> = {
    active: { label: 'Active', tone: 'success' },
    disabled: { label: 'Disabled', tone: 'neutral', hint: 'Turned off; it can be turned on again.' },
    revoked: { label: 'Revoked', tone: 'error', hint: 'Stopped for good: a revoked key never works again.' },
};

export const apiKeyStatus = (value: string | null | undefined): Label => lookup(API_KEY_STATUSES, value);

/** What an import key (scope tour_operator) may read through import/query and the MCP query tools. */
export const LEGACY_ACCESS_FLAGS: Record<string, string> = {
    activities: 'Activities',
    itineraries: 'Itineraries',
    tours: 'Tours',
    users: 'Users',
    tour_participants: 'Tour participants',
    tag_categories: 'Tag categories',
    tags: 'Tags',
};

// The production Overview's sandbox card (DSEC D3; developer.openapi.yaml SandboxCard).
export const SANDBOX_STATUSES: Record<string, Label> = {
    active: { label: 'Active', tone: 'success' },
    paused: { label: 'Paused', tone: 'warning', hint: 'The circuit breaker holds its inbound requests until a reset.' },
    suspended: { label: 'Suspended', tone: 'error', hint: 'It imports nothing, sends nothing and refuses writes; sign-in waits until it is resumed.' },
};
export const sandboxStatus = (value: string | null | undefined) => lookup(SANDBOX_STATUSES, value);

export const SANDBOX_DATA_SOURCES: Record<string, Label> = {
    production: { label: 'Production data', tone: 'warning', hint: "It uses production's vendor key or mirrors production's webhooks: its bookings are real." },
    test: { label: 'Test data', tone: 'neutral', hint: 'It holds test credentials only.' },
};
export const sandboxDataSource = (value: string | null | undefined) => lookup(SANDBOX_DATA_SOURCES, value);

export const SMS_STATES: Record<string, Label> = {
    off: { label: 'Off', tone: 'neutral', hint: 'No number: every SMS and WhatsApp message is kept in the outbox.' },
    setting_up: { label: 'Being set up', tone: 'warning', hint: 'Setting up did not finish: set it up again to continue.' },
    on: { label: 'On', tone: 'success', hint: 'The sandbox sends and receives SMS from its own number.' },
    removing: { label: 'Being removed', tone: 'warning', hint: 'Removing did not finish: remove it again.' },
};
export const smsState = (value: string | null | undefined) => lookup(SMS_STATES, value);
